// Nhận lời mời từ link trong email: đặt mật khẩu → tham gia đơn vị. Khung chung components/auth/AuthStage.tsx.

import { useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, post } from '@/lib/api'
import { Spinner } from '@/components/ui/spinner'
import { AuthStage, AuthTitle } from '@/components/auth/AuthStage'
import { AuthError, AuthPassword, AuthSubmit, finePointer } from '@/components/auth/AuthFields'

const MIN = 10
const ROLE: Record<string, string> = { owner: 'Chủ đơn vị', staff: 'Nhân viên' }

export default function AcceptInvitePage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const nav = useNavigate()
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mismatch = pw2.length > 0 && pw !== pw2

  const preview = useQuery({
    queryKey: ['invite.preview', token],
    queryFn: () => get<{ email: string; role: string; shop: { name: string; id: string }; needsPassword: boolean }>(`/auth/invite/preview?token=${encodeURIComponent(token)}`),
    enabled: !!token,
    retry: false,
  })

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (pw.length < MIN || pw !== pw2) return
    setBusy(true)
    setError(null)
    try {
      await post('/auth/invite/accept', { token, password: pw })
      toast.success('Đã tham gia — đăng nhập ngay nhé')
      nav('/login', { replace: true })
    } catch (err) {
      setError((err as Error).message || 'Không nhận được lời mời, thử lại sau')
    } finally {
      setBusy(false)
    }
  }

  const invite = preview.data
  return (
    <AuthStage homeLink="/login">
      {!token || preview.isError ? (
        <>
          <AuthTitle sub="Lời mời đã hết hạn hoặc đã được dùng.">Lời mời không hợp lệ</AuthTitle>
          <Link to="/login" className="lg-btn2">Về đăng nhập</Link>
        </>
      ) : (
        <form onSubmit={onSubmit} noValidate>
          <AuthTitle
            sub={invite ? <>Vào <b>{invite.shop.name}</b> · vai trò <b>{ROLE[invite.role] ?? invite.role}</b><br />{invite.email}</> : null}
          >
            Nhận lời mời
          </AuthTitle>
          {preview.isLoading && <p className="lg-status"><Spinner /> Đang tải lời mời…</p>}
          <AuthPassword id="invite-pw" label="Mật khẩu" note={`Tối thiểu ${MIN} ký tự`} value={pw} onChange={setPw} autoFocus={finePointer()} />
          <AuthPassword id="invite-pw2" label="Nhập lại" value={pw2} onChange={setPw2} invalid={mismatch}>
            {mismatch && <p className="lg-hint">Hai mật khẩu chưa khớp</p>}
          </AuthPassword>
          {error && <AuthError>{error}</AuthError>}
          <AuthSubmit busy={busy} busyLabel="Đang xử lý…" disabled={pw.length < MIN || pw !== pw2}>Tham gia</AuthSubmit>
        </form>
      )}
    </AuthStage>
  )
}
